import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { PRIMARY_NAV } from "@/lib/brand";
import EventName from "@/components/EventName";

// Compute the Arabic counterpart URL for the current English path.
// English root "/" -> "/ar". English "/foo/bar" -> "/ar/foo/bar".
function toArabicPath(pathname) {
    if (pathname === "/" || pathname === "") return "/ar";
    return `/ar${pathname.startsWith("/") ? "" : "/"}${pathname}`;
}

// Render a nav label. When the label is the official CrewZNatioN event
// name ("The Gathering") we render the protected wordmark via EventName —
// guaranteeing THE white / GATHERING gold, uppercase, LTR isolated. Any
// other label containing "Gathering" (defensive) still receives the gold
// treatment on that single word while preserving the surrounding text.
function renderNavLabel(label) {
    if (label.trim().toLowerCase() === "the gathering") {
        return <EventName />;
    }
    const idx = label.toLowerCase().indexOf("gathering");
    if (idx === -1) return label;
    const before = label.slice(0, idx);
    const word = label.slice(idx, idx + "gathering".length);
    const after = label.slice(idx + "gathering".length);
    return (
        <>
            {before}
            <span className="text-[var(--crewz-gold)]">{word}</span>
            {after}
        </>
    );
}

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const arabicHref = toArabicPath(location.pathname);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            className="sticky top-0 z-50 bg-[#111111]/90 backdrop-blur-xl border-b border-white/5"
            data-testid="site-navbar"
        >
            <div className="px-6 md:px-10 lg:px-16 h-24 flex items-center justify-between">
                <nav className="hidden lg:flex flex-1 items-center justify-between gap-1 font-body text-[13px] tracking-[0.2em] uppercase font-bold order-2 lg:mr-4 xl:mr-6 2xl:mr-16">
                    {PRIMARY_NAV.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            className={({ isActive }) =>
                                `px-2 py-2 text-center transition-colors duration-300 ${
                                    isActive
                                        ? "text-[var(--crewz-gold)]"
                                        : "text-zinc-200 hover:text-white"
                                }`
                            }
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s|&/g, "-")}`}
                        >
                            {renderNavLabel(l.label)}
                        </NavLink>
                    ))}
                    <Link
                        to={arabicHref}
                        className="font-arabic normal-case tracking-normal px-2 py-2 text-[17px] leading-none font-bold text-zinc-200 hover:text-white transition-colors duration-300 self-center relative -top-[0.5px]"
                        data-testid="nav-lang-switcher"
                        aria-label="التبديل إلى العربية"
                    >
                        العربية
                    </Link>
                </nav>

                <button
                    className="lg:hidden text-white p-2 order-2"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="nav-mobile-toggle"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    className="lg:hidden border-t border-white/5 bg-[#111111]/95 backdrop-blur-xl"
                    data-testid="nav-mobile-menu"
                >
                    <div className="px-6 py-6 flex flex-col gap-1 font-body">
                        {PRIMARY_NAV.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                className={({ isActive }) =>
                                    `px-2 py-3 text-xs tracking-[0.22em] uppercase border-b border-white/5 ${
                                        isActive
                                            ? "text-[var(--crewz-gold)]"
                                            : "text-zinc-300"
                                    }`
                                }
                            >
                                {renderNavLabel(l.label)}
                            </NavLink>
                        ))}
                        <Link
                            to={arabicHref}
                            className="font-arabic normal-case tracking-normal px-2 py-3 text-[15px] font-bold text-zinc-300 border-b border-white/5"
                            data-testid="nav-mobile-lang-switcher"
                            aria-label="التبديل إلى العربية"
                        >
                            العربية
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
